import React, { useRef, useEffect, useState, useCallback } from 'react';

const VideoTracker = ({ src, onVideoProgress, onThresholdReached, threshold = 90, videoProps = {}, children }) => {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [watchedChunks, setWatchedChunks] = useState(new Set());
  const [lastTime, setLastTime] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [thresholdReached, setThresholdReached] = useState(false);
  const chunkSize = 1; // 1 second chunks

  // Calculate the percentage of video watched based on chunks
  const getWatchedPercentage = useCallback(() => {
    if (duration === 0) return 0;

    const totalChunks = Math.ceil(duration / chunkSize);
    const watchedCount = watchedChunks.size;
    return (watchedCount / totalChunks) * 100;
  }, [watchedChunks, duration, chunkSize]);

  const checkWatchedPercentage = useCallback(() => {
    if (duration === 0) return 0;

    const percent = getWatchedPercentage();

    // Call progress callback if provided
    if (onVideoProgress) {
      onVideoProgress(percent);
    }

    // Call threshold callback if threshold is reached and not already triggered
    if (percent >= threshold && !thresholdReached) {
      setThresholdReached(true);
      if (onThresholdReached) {
        onThresholdReached(percent);
      }
    }

    return percent;
  }, [duration, getWatchedPercentage, onVideoProgress, onThresholdReached, threshold, thresholdReached]);

  const addWatchedChunk = useCallback(
    (time) => {
      if (time < 0 || time > duration || !duration) return;

      const chunk = Math.floor(time / chunkSize);

      setWatchedChunks((prev) => {
        if (prev.has(chunk)) return prev;
        const updated = new Set(prev);
        updated.add(chunk);
        return updated;
      });
    },
    [duration, chunkSize]
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const onTimeUpdate = () => {
      if (seeking) return;

      const { currentTime } = video;

      addWatchedChunk(currentTime);

      setLastTime(currentTime);
      checkWatchedPercentage();
    };

    const onSeeking = () => {
      setSeeking(true);
    };

    const onSeeked = () => {
      setSeeking(false);
      addWatchedChunk(video.currentTime);
      checkWatchedPercentage();
    };

    const onPause = () => {
      addWatchedChunk(video.currentTime);
      checkWatchedPercentage();
    };

    const onPlay = () => {
      addWatchedChunk(video.currentTime);
    };

    const onEnded = () => {
      addWatchedChunk(video.duration);
      checkWatchedPercentage();
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('pause', onPause);
    video.addEventListener('play', onPlay);
    video.addEventListener('ended', onEnded);

    if (video.currentTime > 0) {
      addWatchedChunk(video.currentTime);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (video.currentTime > 0) {
        addWatchedChunk(video.currentTime);
      }

      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('ended', onEnded);
    };
  }, [lastTime, seeking, addWatchedChunk, checkWatchedPercentage]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video ref={videoRef} src={src} {...videoProps}>
      {children}
    </video>
  );
};

export default VideoTracker;
